import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  BeneficiariosPorUsuario,
  BeneficiarioOrigen,
  RefreshVitaResponse,
  SearchBeneficiariosResponse,
  SearchUsersResponse,
} from '../interfaces/registro-beneficiarios.interface';

/**
 * Service del "Registro de beneficiarios" (consulta de operador, solo lectura).
 *
 * Apunta a `beneficiarios-lookup` en el backend NestJS. Todas las llamadas van
 * por `HttpClient`, así que el `ApiRestInterceptor` adjunta el idToken de
 * Cognito automáticamente. El backend exige el grupo `aguacate_operator`.
 */
@Injectable({ providedIn: 'root' })
export class RegistroBeneficiariosService {
  private baseUrl = `${environment.apiNest}beneficiarios-lookup`;

  constructor(private http: HttpClient) {}

  /** Dirección cliente→beneficiario: busca al cliente por RUT/correo/ID. */
  searchUsers(filters: {
    q?: string;
    usuarioId?: number;
    page?: number;
    limit?: number;
  }): Promise<SearchUsersResponse> {
    const params = this.buildParams(filters);
    return firstValueFrom(
      this.http.get<SearchUsersResponse>(`${this.baseUrl}/users`, { params }),
    );
  }

  /** Beneficiarios de un cliente puntual, agrupados por origen. */
  getByUser(usuarioId: number): Promise<BeneficiariosPorUsuario> {
    return firstValueFrom(
      this.http.get<BeneficiariosPorUsuario>(
        `${this.baseUrl}/by-user/${usuarioId}`,
      ),
    );
  }

  /**
   * Re-verifica contra Vita el estado AML de un beneficiario Vita (por su `id`
   * local). Acción de operador; POST (no PATCH por CORS).
   */
  refreshVita(beneficiarioId: string): Promise<RefreshVitaResponse> {
    return firstValueFrom(
      this.http.post<RefreshVitaResponse>(
        `${this.baseUrl}/vita/${beneficiarioId}/refresh-vita`,
        {},
      ),
    );
  }

  /** Dirección beneficiario→cliente: busca directamente sobre beneficiarios. */
  searchBeneficiarios(filters: {
    q: string;
    origen?: BeneficiarioOrigen;
    page?: number;
    limit?: number;
  }): Promise<SearchBeneficiariosResponse> {
    const params = this.buildParams(filters);
    return firstValueFrom(
      this.http.get<SearchBeneficiariosResponse>(`${this.baseUrl}/search`, {
        params,
      }),
    );
  }

  private buildParams(obj: Record<string, any>): Record<string, string> {
    const out: Record<string, string> = {};
    for (const k of Object.keys(obj)) {
      const v = obj[k];
      if (v === undefined || v === null || v === '') continue;
      out[k] = String(v);
    }
    return out;
  }
}
